import React, { useState, useEffect } from "react";
import axios from "axios";
import "./AdminPanel.css";

const AdminPanel = () => {
  const [questions, setQuestions] = useState([]);
  const [questionTypes, setQuestionTypes] = useState([]);
  const [formData, setFormData] = useState({
    question_text: "",
    question_text_bangla: "",
    image: "",
    option_1: "",
    option_1_bangla: "",
    option_2: "",
    option_2_bangla: "",
    option_3: "",
    option_3_bangla: "",
    option_4: "",
    option_4_bangla: "",
    correct_answer: "",
    explanation: "",
    explanation_bangla: "",
    question_type_id: "",
  });
  const [filterType, setFilterType] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedQuestionId, setSelectedQuestionId] = useState(null);
  
  // Tabs for the Editor section: "edit" or "simulator"
  const [editorTab, setEditorTab] = useState("edit");
  // Language toggle for the Simulator preview: "en" or "bn"
  const [simulatorLang, setSimulatorLang] = useState("en");

  // Notifications
  const [alert, setAlert] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchQuestions();
    fetchQuestionTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterType]);

  const showAlert = (message, type = "success") => {
    setAlert({ message, type });
    setTimeout(() => setAlert(null), 4000);
  };

  const fetchQuestions = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/admin-get-questions`,
        {
          params: { question_type_id: filterType || undefined },
        }
      );
      setQuestions(response.data);
    } catch (error) {
      console.error("Error fetching questions:", error);
      showAlert("Error loading questions", "error");
    } finally {
      setLoading(false);
    }
  };

  const fetchQuestionTypes = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/admin-get-question-types`
      );
      setQuestionTypes(response.data);
    } catch (error) {
      console.error("Error fetching question types:", error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const selectQuestionForEdit = (question) => {
    setSelectedQuestionId(question.question_id);
    // Auto-populate correct_answer from DB (0-3) to UI (1-4)
    const uiCorrectAnswer = 
      question.correct_answer !== null && question.correct_answer !== undefined 
        ? question.correct_answer + 1 
        : "";

    setFormData({
      ...question,
      correct_answer: uiCorrectAnswer,
    });
    setEditorTab("edit");
  };

  const handleResetForm = () => {
    setSelectedQuestionId(null);
    setFormData({
      question_text: "",
      question_text_bangla: "",
      image: "",
      option_1: "",
      option_1_bangla: "",
      option_2: "",
      option_2_bangla: "",
      option_3: "",
      option_3_bangla: "",
      option_4: "",
      option_4_bangla: "",
      correct_answer: "",
      explanation: "",
      explanation_bangla: "",
      question_type_id: filterType || "",
    });
    setEditorTab("edit");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.question_type_id) {
      showAlert("Please select a question type", "error");
      return;
    }

    if (!formData.correct_answer) {
      showAlert("Please choose the correct answer option", "error");
      return;
    }

    // Convert UI correct_answer (1-4) to DB index (0-3)
    const dbCorrectAnswer = parseInt(formData.correct_answer, 10) - 1;
    const submissionData = {
      ...formData,
      correct_answer: dbCorrectAnswer,
    };

    setLoading(true);
    try {
      if (selectedQuestionId) {
        // Edit existing question
        await axios.put(
          `${process.env.REACT_APP_API_URL}/admin-edit-question/${selectedQuestionId}`,
          submissionData
        );
        showAlert("Question updated successfully!");
      } else {
        // Create new question
        await axios.post(
          `${process.env.REACT_APP_API_URL}/admin-add-question`,
          submissionData
        );
        showAlert("New question created successfully!");
      }
      fetchQuestions();
      handleResetForm();
    } catch (error) {
      console.error("Error saving question:", error);
      showAlert("Failed to save question", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (questionId) => {
    if (window.confirm("Are you sure you want to permanently delete this question?")) {
      setLoading(true);
      try {
        await axios.delete(
          `${process.env.REACT_APP_API_URL}/admin-delete-question/${questionId}`
        );
        showAlert("Question deleted successfully!");
        fetchQuestions();
        if (selectedQuestionId === questionId) {
          handleResetForm();
        }
      } catch (error) {
        console.error("Error deleting question:", error);
        showAlert("Failed to delete question", "error");
      } finally {
        setLoading(false);
      }
    }
  };

  // Filter list of questions by search query
  const filteredQuestions = questions.filter((q) => {
    const term = searchTerm.toLowerCase().trim();
    if (!term) return true;
    
    const idMatches = String(q.question_id).includes(term);
    const textMatches = q.question_text && q.question_text.toLowerCase().includes(term);
    const banglaMatches = q.question_text_bangla && q.question_text_bangla.toLowerCase().includes(term);
    
    return idMatches || textMatches || banglaMatches;
  });

  const getTypeName = (typeId) => {
    const typeObj = questionTypes.find((t) => t.question_type_id === parseInt(typeId, 10));
    return typeObj ? typeObj.name : `Type ${typeId}`;
  };

  return (
    <div className="admin-panel-container min-h-screen bg-slate-50 font-sans text-slate-800 antialiased pb-12">
      {/* Toast Alert Banner */}
      {alert && (
        <div className={`fixed top-5 right-5 z-50 flex items-center px-4 py-3 rounded-lg shadow-xl text-white font-medium transition-all duration-300 transform translate-y-0 ${
          alert.type === "error" ? "bg-rose-600" : "bg-emerald-600"
        }`}>
          <span>{alert.message}</span>
        </div>
      )}

      {/* Header Area */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-30 px-6 py-4 shadow-sm">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="flex items-center space-x-3">
            <div className="bg-indigo-600 text-white p-2 rounded-lg">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <div>
              <div className="text-xl font-bold text-slate-900">DVSA Questions Manager</div>
              <p className="text-xs text-slate-500">Edit, preview and organize test questions</p>
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <span className="text-xs text-slate-500 font-medium">
              Database: <span className="text-indigo-600 font-semibold">{questions.length} Questions</span>
            </span>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
          
          {/* LEFT COLUMN: Search, Filters & Questions Preview List */}
          <div className="lg:col-span-5 bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden flex flex-col h-[calc(100vh-140px)]">
            
            {/* Filters panel */}
            <div className="p-4 border-b border-slate-100 bg-slate-50 space-y-3">
              <div className="flex justify-between items-center">
                <div className="font-semibold text-slate-700 text-sm">Question List</div>
                <button
                  type="button"
                  onClick={handleResetForm}
                  className="px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-colors shadow-sm"
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 4v16m8-8H4" />
                  </svg>
                  Add Question
                </button>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {/* Search Bar */}
                <div className="relative">
                  <span className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-slate-400">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </span>
                  <input
                    type="text"
                    placeholder="Search ID or text..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-9 pr-3 py-1.5 bg-white border border-slate-300 rounded-lg text-xs focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 text-slate-800 shadow-inner"
                  />
                </div>

                {/* Category Selector */}
                <div>
                  <select
                    value={filterType}
                    onChange={(e) => setFilterType(e.target.value)}
                    className="w-full py-1.5 px-2 bg-white border border-slate-300 rounded-lg text-xs focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 text-slate-700 cursor-pointer"
                  >
                    <option value="">All Categories</option>
                    {questionTypes.map((type) => (
                      <option key={type.question_type_id} value={type.question_type_id}>
                        {type.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex justify-between items-center text-[10px] text-slate-500 font-medium">
                <span>Showing {filteredQuestions.length} of {questions.length} questions</span>
                {filterType && (
                  <button 
                    onClick={() => setFilterType("")}
                    className="text-indigo-600 hover:text-indigo-800 hover:underline"
                  >
                    Clear Filter
                  </button>
                )}
              </div>
            </div>

            {/* Questions scroll container */}
            <div className="flex-1 overflow-y-auto divide-y divide-slate-100 bg-white list-container">
              {loading && questions.length === 0 ? (
                <div className="p-8 text-center text-slate-400">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto mb-2"></div>
                  <p className="text-xs">Loading database questions...</p>
                </div>
              ) : filteredQuestions.length > 0 ? (
                filteredQuestions.map((q) => {
                  const isSelected = selectedQuestionId === q.question_id;
                  return (
                    <div
                      key={q.question_id}
                      onClick={() => selectQuestionForEdit(q)}
                      className={`p-3.5 text-left transition-all cursor-pointer relative flex flex-col gap-2 hover:bg-slate-50 ${
                        isSelected 
                          ? "bg-indigo-50/70 border-l-4 border-indigo-600 pl-2.5" 
                          : "border-l-4 border-transparent"
                      }`}
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex items-center gap-1.5">
                          <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${
                            isSelected ? "bg-indigo-200 text-indigo-900" : "bg-slate-200 text-slate-700"
                          }`}>
                            #{q.question_id}
                          </span>
                          <span className="text-[10px] font-medium text-slate-500 truncate max-w-[150px]">
                            {getTypeName(q.question_type_id)}
                          </span>
                        </div>
                        <div className="flex items-center gap-1">
                          {q.image && (
                            <span className="bg-sky-50 text-sky-700 border border-sky-200 text-[9px] font-bold px-1 rounded flex items-center gap-0.5">
                              <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                              </svg>
                              Image
                            </span>
                          )}
                        </div>
                      </div>

                      {/* English Text preview */}
                      <p className="text-xs font-semibold text-slate-800 line-clamp-2 leading-relaxed">
                        {q.question_text || "(No English text)"}
                      </p>

                      {/* Bangla Text preview */}
                      {q.question_text_bangla && (
                        <p className="text-[11px] text-slate-500 bangla-text line-clamp-1 leading-relaxed border-t border-slate-100/50 pt-1">
                          {q.question_text_bangla}
                        </p>
                      )}
                    </div>
                  );
                })
              ) : (
                <div className="p-12 text-center text-slate-400">
                  <svg className="w-8 h-8 mx-auto text-slate-300 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className="text-xs font-semibold">No questions found</p>
                  <p className="text-[10px] text-slate-400 mt-1">Try adapting your search or filter settings.</p>
                </div>
              )}
            </div>
          </div>

          {/* RIGHT COLUMN: Tabbed Editor and Preview Simulator */}
          <div className="lg:col-span-7 flex flex-col">
            
            {/* Tab header buttons */}
            <div className="flex border-b border-slate-200 mb-4 bg-white p-1 rounded-lg border shadow-sm">
              <button
                type="button"
                onClick={() => setEditorTab("edit")}
                className={`flex-1 py-2 px-4 rounded-md text-xs font-bold transition-all flex items-center justify-center gap-2 ${
                  editorTab === "edit"
                    ? "bg-indigo-600 text-white shadow-sm"
                    : "text-slate-600 hover:text-slate-900 hover:bg-slate-50"
                }`}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                {selectedQuestionId ? `Edit Question #${selectedQuestionId}` : "Create New Question"}
              </button>
              <button
                type="button"
                onClick={() => setEditorTab("simulator")}
                className={`flex-1 py-2 px-4 rounded-md text-xs font-bold transition-all flex items-center justify-center gap-2 ${
                  editorTab === "simulator"
                    ? "bg-indigo-600 text-white shadow-sm"
                    : "text-slate-600 hover:text-slate-900 hover:bg-slate-50"
                }`}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
                Live Simulator Preview
              </button>
            </div>

            {/* TAB CONTENT: EDIT FORM */}
            {editorTab === "edit" && (
              <form onSubmit={handleSubmit} className="bg-white border border-slate-200 rounded-xl shadow-sm p-6 space-y-6 text-left">
                
                {/* Form Heading & State */}
                <div className="flex justify-between items-center pb-4 border-b border-slate-100">
                  <div>
                    <div className="font-bold text-slate-800 text-base">
                      {selectedQuestionId ? "Update Question Details" : "New Question Details"}
                    </div>
                    <p className="text-xs text-slate-400">Provide bilingual content and choose the correct answer</p>
                  </div>
                  {selectedQuestionId && (
                    <button
                      type="button"
                      onClick={() => handleDelete(selectedQuestionId)}
                      className="px-3 py-1.5 bg-rose-50 hover:bg-rose-100 border border-rose-200 text-rose-700 text-xs font-bold rounded-lg transition-colors flex items-center gap-1.5"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                      Delete Question
                    </button>
                  )}
                </div>

                {/* Grid layout for basic info */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Question Category Select */}
                  <div className="flex flex-col gap-1">
                    <label className="text-xs font-bold text-slate-700">Question Category *</label>
                    <select
                      name="question_type_id"
                      value={formData.question_type_id || ""}
                      onChange={handleInputChange}
                      required
                      className="py-2 px-3 bg-white border border-slate-300 rounded-lg text-xs focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 text-slate-800"
                    >
                      <option value="">Select Category</option>
                      {questionTypes.map((type) => (
                        <option key={type.question_type_id} value={type.question_type_id}>
                          {type.name} (ID: {type.question_type_id})
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Image URL input & fast thumbnail preview */}
                  <div className="flex flex-col gap-1">
                    <label className="text-xs font-bold text-slate-700">Image URL (Optional)</label>
                    <input
                      type="text"
                      name="image"
                      value={formData.image || ""}
                      onChange={handleInputChange}
                      placeholder="e.g. https://images.com/diagram.jpg"
                      className="py-1.5 px-3 border border-slate-300 rounded-lg text-xs focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 text-slate-800"
                    />
                    {formData.image && (
                      <div className="mt-2 flex items-center gap-2 border border-slate-100 p-1.5 rounded-lg bg-slate-50 max-w-fit">
                        <img 
                          src={formData.image} 
                          alt="Thumbnail Preview" 
                          className="h-10 w-16 object-cover rounded border border-slate-200"
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                        <span className="text-[10px] text-slate-400">Live URL Loaded</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Bilingual Question Text area */}
                <div className="space-y-4">
                  <div className="flex flex-col gap-1">
                    <label className="text-xs font-bold text-slate-700 flex items-center justify-between">
                      <span>Question Text (English) *</span>
                      <span className="text-[10px] text-slate-400 font-normal">Displayed on main testing page</span>
                    </label>
                    <textarea
                      name="question_text"
                      value={formData.question_text || ""}
                      onChange={handleInputChange}
                      required
                      rows="2"
                      placeholder="Type the question in English..."
                      className="py-2 px-3 border border-slate-300 rounded-lg text-xs focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 text-slate-800 shadow-inner"
                    />
                  </div>

                  <div className="flex flex-col gap-1">
                    <label className="text-xs font-bold text-slate-700">Question Text (Bangla)</label>
                    <textarea
                      name="question_text_bangla"
                      value={formData.question_text_bangla || ""}
                      onChange={handleInputChange}
                      rows="2"
                      placeholder="Type the question in Bangla..."
                      className="py-2 px-3 border border-slate-300 rounded-lg text-xs focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 text-slate-800 shadow-inner bangla-text"
                    />
                  </div>
                </div>

                {/* Options Layout */}
                <div className="space-y-4 pt-4 border-t border-slate-100">
                  <div className="flex justify-between items-center">
                    <div className="text-xs font-bold text-slate-700 uppercase tracking-wider">Answer Options & Correct Key</div>
                    <span className="text-[10px] text-slate-400">Select one option as correct</span>
                  </div>

                  <div className="space-y-3.5">
                    {["option_1", "option_2", "option_3", "option_4"].map((optKey, index) => {
                      const optNum = index + 1;
                      const isCorrect = parseInt(formData.correct_answer, 10) === optNum;

                      return (
                        <div 
                          key={optKey} 
                          className={`p-3 rounded-lg border transition-all ${
                            isCorrect 
                              ? "bg-emerald-50/50 border-emerald-300 ring-1 ring-emerald-300" 
                              : "bg-slate-50 border-slate-200"
                          }`}
                        >
                          <div className="flex justify-between items-center mb-2">
                            <span className="text-xs font-bold text-slate-700">Option {optNum}</span>
                            
                            {/* Correct Option Radio Button */}
                            <label className="flex items-center gap-1.5 cursor-pointer select-none">
                              <input
                                type="radio"
                                name="correct_answer_radio"
                                checked={isCorrect}
                                onChange={() => setFormData({ ...formData, correct_answer: optNum })}
                                className="h-4 w-4 text-emerald-600 focus:ring-emerald-500 border-slate-300"
                              />
                              <span className={`text-[11px] font-bold ${
                                isCorrect ? "text-emerald-700" : "text-slate-500"
                              }`}>
                                Mark Correct Answer
                              </span>
                            </label>
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div className="flex flex-col gap-1">
                              <span className="text-[10px] text-slate-500 font-medium">English Value</span>
                              <input
                                type="text"
                                name={optKey}
                                value={formData[optKey] || ""}
                                onChange={handleInputChange}
                                placeholder={`English option ${optNum}`}
                                className="py-1.5 px-2 bg-white border border-slate-300 rounded text-xs focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                              />
                            </div>
                            <div className="flex flex-col gap-1">
                              <span className="text-[10px] text-slate-500 font-medium">Bangla Value</span>
                              <input
                                type="text"
                                name={`${optKey}_bangla`}
                                value={formData[`${optKey}_bangla`] || ""}
                                onChange={handleInputChange}
                                placeholder={`Bangla option ${optNum}`}
                                className="py-1.5 px-2 bg-white border border-slate-300 rounded text-xs focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 bangla-text"
                              />
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Explanation text */}
                <div className="space-y-4 pt-4 border-t border-slate-100">
                  <div className="text-xs font-bold text-slate-700 uppercase tracking-wider">Bilingual Explanations</div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex flex-col gap-1">
                      <label className="text-xs font-bold text-slate-600">Explanation (English)</label>
                      <textarea
                        name="explanation"
                        value={formData.explanation || ""}
                        onChange={handleInputChange}
                        rows="3"
                        placeholder="Provide details behind correct answer..."
                        className="py-1.5 px-3 border border-slate-300 rounded-lg text-xs focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 text-slate-800"
                      />
                    </div>
                    
                    <div className="flex flex-col gap-1">
                      <label className="text-xs font-bold text-slate-600">Explanation (Bangla)</label>
                      <textarea
                        name="explanation_bangla"
                        value={formData.explanation_bangla || ""}
                        onChange={handleInputChange}
                        rows="3"
                        placeholder="Bangla explanation content..."
                        className="py-1.5 px-3 border border-slate-300 rounded-lg text-xs focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 text-slate-800 bangla-text"
                      />
                    </div>
                  </div>
                </div>

                {/* Actions container */}
                <div className="flex justify-end gap-3 pt-5 border-t border-slate-100">
                  <button
                    type="button"
                    onClick={handleResetForm}
                    className="px-4 py-2 border border-slate-300 hover:bg-slate-50 text-slate-700 text-xs font-bold rounded-lg transition-colors"
                  >
                    Clear / Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-5 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold rounded-lg transition-all shadow-md"
                  >
                    {selectedQuestionId ? "Update Question" : "Create Question"}
                  </button>
                </div>

              </form>
            )}

            {/* TAB CONTENT: LIVE SIMULATOR */}
            {editorTab === "simulator" && (
              <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6 text-left space-y-6">
                
                {/* Simulator settings bar */}
                <div className="flex justify-between items-center pb-4 border-b border-slate-100">
                  <div>
                    <div className="font-bold text-slate-800 text-base">User-facing Practice Simulator</div>
                    <p className="text-xs text-slate-400">Renders the current question exactly as users see it</p>
                  </div>

                  {/* Bilingual Simulator Switcher */}
                  <div className="flex items-center gap-1 bg-slate-100 p-0.5 rounded-lg border border-slate-200">
                    <button
                      type="button"
                      onClick={() => setSimulatorLang("en")}
                      className={`px-3 py-1 rounded text-[11px] font-bold transition-all ${
                        simulatorLang === "en"
                          ? "bg-white text-indigo-700 shadow-sm"
                          : "text-slate-500 hover:text-slate-800"
                      }`}
                    >
                      English
                    </button>
                    <button
                      type="button"
                      onClick={() => setSimulatorLang("bn")}
                      className={`px-3 py-1 rounded text-[11px] font-bold transition-all ${
                        simulatorLang === "bn"
                          ? "bg-white text-indigo-700 shadow-sm"
                          : "text-slate-500 hover:text-slate-800"
                      }`}
                    >
                      বাংলা
                    </button>
                  </div>
                </div>

                {/* Simulator Frame Wrapper */}
                <div className="bg-slate-900 text-white rounded-xl p-5 shadow-inner min-h-[400px] flex flex-col justify-between">
                  <div>
                    {/* Simulator Header */}
                    <div className="flex justify-between items-center text-[10px] text-slate-400 font-semibold mb-4 border-b border-slate-800 pb-2">
                      <span>PRACTICE MODE SIMULATION</span>
                      <span className="bg-indigo-500/20 text-indigo-300 border border-indigo-500/30 px-2 py-0.5 rounded uppercase">
                        {formData.question_type_id ? getTypeName(formData.question_type_id) : "Uncategorized"}
                      </span>
                    </div>

                    {/* Question text */}
                    <div className="space-y-4">
                      <div className={`text-sm md:text-base font-medium leading-relaxed ${
                        simulatorLang === "bn" ? "bangla-text text-[15px]" : ""
                      }`}>
                        {simulatorLang === "en" 
                          ? (formData.question_text || "Question text will appear here.") 
                          : (formData.question_text_bangla || formData.question_text || "বাংলা প্রশ্ন এখানে প্রদর্শিত হবে।")
                        }
                      </div>

                      {/* Image container */}
                      {formData.image ? (
                        <div className="w-full max-h-48 overflow-hidden rounded-lg border border-slate-800 bg-black flex justify-center">
                          <img 
                            src={formData.image} 
                            alt="Question Diagram" 
                            className="max-h-48 object-contain"
                          />
                        </div>
                      ) : null}
                    </div>

                    {/* Options Buttons */}
                    <div className="mt-6 space-y-2.5">
                      {[1, 2, 3, 4].map((optNum) => {
                        const optKey = `option_${optNum}`;
                        const optBanglaKey = `option_${optNum}_bangla`;
                        
                        const textEN = formData[optKey];
                        const textBN = formData[optBanglaKey];
                        
                        const textToShow = simulatorLang === "en" ? textEN : (textBN || textEN);
                        const isCorrect = parseInt(formData.correct_answer, 10) === optNum;

                        return (
                          <div
                            key={optNum}
                            className={`w-full p-3 rounded-lg border text-left text-xs transition-all flex items-center justify-between ${
                              isCorrect 
                                ? "bg-emerald-950/30 border-emerald-500 text-emerald-100 font-medium" 
                                : "bg-slate-800/50 border-slate-700 text-slate-300"
                            }`}
                          >
                            <span className={simulatorLang === "bn" ? "bangla-text" : ""}>
                              {optNum}. {textToShow || `Option ${optNum}`}
                            </span>
                            {isCorrect && (
                              <span className="bg-emerald-500 text-slate-900 rounded-full p-0.5 flex items-center justify-center">
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                                </svg>
                              </span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Simulator Explanation Drawer */}
                  <div className="mt-6 pt-4 border-t border-slate-800 space-y-2">
                    <div className="text-[10px] text-indigo-400 font-bold uppercase tracking-wider">Correct Answer Explanation</div>
                    <p className={`text-xs text-slate-300 leading-relaxed ${
                      simulatorLang === "bn" ? "bangla-text text-[13px]" : ""
                    }`}>
                      {simulatorLang === "en"
                        ? (formData.explanation || "No explanation provided yet.")
                        : (formData.explanation_bangla || formData.explanation || "কোন ব্যাখ্যা প্রদান করা হয়নি।")
                      }
                    </p>
                  </div>
                </div>

                {/* Toggle help message */}
                <div className="text-center bg-slate-50 border border-slate-200 rounded-lg p-3 text-[11px] text-slate-500">
                  ⚡ <strong>Note:</strong> Renders correct options in green. Make edits in the <strong>Editor Tab</strong> to observe hot-reloaded changes here immediately.
                </div>

              </div>
            )}

          </div>

        </div>
      </main>
    </div>
  );
};

export default AdminPanel;